import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getAllCategory } from "../../redux/apiCall/categoryCall";
import axios from "axios";
import AdminFormLayout from "../components/AdminFormLayout";
import ButtonComponent from "../../components/ButtonComponent";
import ColorPalettePicker from "../components/ColorPalettePicker";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { apiUrl } from "../../config/api";
import { isPaletteColor, resolveColorHex } from "../../config/colorPalette";
import {
  appendColors,
  appendImages,
  appendKeepImages,
  ensureAdminAuth,
  getApiErrorMessage,
  normalizeCategoryId,
} from "../../utils/adminApi";

const EditProduct = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.category.categories);

  const productId = location.state?.productId;

  const [loading, setLoading] = useState(true);
  const [existingImages, setExistingImages] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    categoryId: "",
    images: [],
  });
  const [colors, setColors] = useState([]);
  const legacyColors = colors.filter((c) => !isPaletteColor(c));
  const paletteColors = colors.filter((c) => isPaletteColor(c));

  const handlePaletteChange = (newPaletteSelection) => {
    setColors([...legacyColors, ...newPaletteSelection]);
  };

  const handleRemoveLegacyColor = (value) => {
    setColors(colors.filter((c) => c !== value));
  };

  useEffect(() => {
    dispatch(getAllCategory());
  }, [dispatch]);

  useEffect(() => {
    if (!productId) {
      toast.error("Product not found.");
      navigate("/admin");
      return;
    }

    const loadProduct = async () => {
      try {
        const { data } = await axios.get(apiUrl(`/api/productsale/${productId}`));
        const product = data.data;

        setFormData({
          name: product.name || "",
          description: product.description || "",
          price: product.price ?? "",
          categoryId: normalizeCategoryId(product.categoryId),
          images: [],
        });
        setExistingImages(product.images || []);
        setColors(Array.isArray(product.color) ? product.color : []);
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to load product"));
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, navigate]);

  const handleImageChange = (e) => {
    const { files } = e.target;
    const newFiles = Array.from(files);

    if (existingImages.length + newFiles.length > 5) {
      toast.error("Maximum 5 images total (current + new).");
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      images: newFiles,
    }));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const headers = ensureAdminAuth();
      const categoryId = normalizeCategoryId(formData.categoryId);

      if (!categoryId) {
        toast.error("Please select a category.");
        return;
      }

      const descriptionText = (formData.description || "").replace(/<[^>]*>/g, "").trim();
      if (!descriptionText) {
        toast.error("Product description is required.");
        return;
      }

      if (existingImages.length + formData.images.length === 0) {
        toast.error("Product must have at least one image.");
        return;
      }

      const productData = new FormData();
      productData.append("name", formData.name);
      productData.append("description", formData.description);
      productData.append("price", formData.price);
      productData.append("categoryId", categoryId);
      appendColors(productData, colors);
      appendKeepImages(productData, existingImages);

      if (formData.images.length > 0) {
        appendImages(productData, formData.images);
      }

      await axios.put(apiUrl(`/api/productsale/${productId}`), productData, {
        headers,
        timeout: 120000,
      });

      toast.success("Product updated successfully");
      navigate("/admin");
    } catch (error) {
      console.error("Update failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to update product"));
    }
  };

  if (loading) {
    return (
      <AdminFormLayout title="Edit Product" backTo="/admin" backLabel="Back to Products">
        <p>Loading product...</p>
      </AdminFormLayout>
    );
  }

  return (
    <AdminFormLayout title="Edit Product" backTo="/admin" backLabel="Back to Products">
      <form encType="multipart/form-data" onSubmit={handleUpdate}>
        <div className="admin-form-group">
          <label htmlFor="name">Product Name</label>
          <input
            type="text"
            className="form-control admin-all-forms-inputs"
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="description">Product Description</label>
          <ReactQuill
            id="description"
            theme="snow"
            value={formData.description}
            onChange={handleDescriptionChange}
            className="admin-all-forms-inputs-quill"
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="price">Product Price</label>
          <input
            type="number"
            className="form-control admin-all-forms-inputs"
            id="price"
            name="price"
            min="0"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            required
          />
        </div>

        {existingImages.length > 0 && (
          <div className="admin-form-group">
            <label>Current Images</label>
            <div className="admin-image-preview-grid">
              {existingImages.map((img, idx) => (
                <div key={`${img}-${idx}`} className="admin-image-preview">
                  <img
                    src={img}
                    alt={`Product ${idx + 1}`}
                    className="admin-table-image"
                    width={80}
                    height={80}
                  />
                  <button
                    type="button"
                    className="admin-image-remove"
                    onClick={() => handleRemoveExistingImage(idx)}
                    aria-label={`Remove image ${idx + 1}`}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <small className="text-muted">
              Click the red X on an image to remove it. Add new ones below (max 5 total).
            </small>
          </div>
        )}

        {formData.images.length > 0 && (
          <div className="admin-form-group">
            <label>New Images to Upload</label>
            <div className="admin-image-preview-grid">
              {formData.images.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="admin-image-preview">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="admin-table-image"
                    width={80}
                    height={80}
                  />
                  <button
                    type="button"
                    className="admin-image-remove"
                    onClick={() => handleRemoveNewImage(idx)}
                    aria-label={`Remove new image ${idx + 1}`}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="admin-form-group">
          <label htmlFor="images">
            Add Product Images ({existingImages.length + formData.images.length}/5)
          </label>
          <input
            type="file"
            className="form-control admin-all-forms-inputs"
            id="images"
            name="images"
            accept="image/*"
            onChange={handleImageChange}
            multiple
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="color">Product Colors</label>
          <ColorPalettePicker selected={paletteColors} onChange={handlePaletteChange} />
          {legacyColors.length > 0 && (
            <>
              <small className="text-muted d-block mt-2">
                Existing colors from before the new palette — remove any you want replaced, then pick from the swatches above.
              </small>
              <div className="legacy-color-tags">
                {legacyColors.map((c, idx) => (
                  <span key={idx} className="legacy-color-tag">
                    <span
                      className="swatch-dot"
                      style={{ backgroundColor: resolveColorHex(c) }}
                    />
                    {c}
                    <button
                      type="button"
                      onClick={() => handleRemoveLegacyColor(c)}
                      aria-label={`Remove ${c}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="admin-form-group">
          <label htmlFor="categoryId">Product Category</label>
          <select
            className="form-select admin-all-forms-inputs"
            id="categoryId"
            name="categoryId"
            value={normalizeCategoryId(formData.categoryId)}
            onChange={(e) =>
              setFormData({ ...formData, categoryId: e.target.value })
            }
            required
          >
            {categories.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <ButtonComponent type="submit">Update Product</ButtonComponent>
      </form>
    </AdminFormLayout>
  );
};

export default EditProduct;
